import { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import BookNowButton from '../BookNowButton';
import { formatPriceParts } from '../../utils/resultDisplay';

function OfferRoute({ left, right, duration, stops }) {
  return (
    <div className="result-offer-card__route">
      <div className="result-offer-card__endpoint">
        <span className="result-offer-card__code">{left.code}</span>
        <span className="result-offer-card__time">{left.time}</span>
      </div>

      <div className="result-offer-card__timeline" aria-hidden>
        <span className="result-offer-card__duration">{duration}</span>
        <span className="result-offer-card__line">
          <span className="result-offer-card__line-dot" />
        </span>
        <span className="result-offer-card__stops">{stops}</span>
      </div>

      <div className="result-offer-card__endpoint result-offer-card__endpoint--end">
        <span className="result-offer-card__code">{right.code}</span>
        <span className="result-offer-card__time">{right.time}</span>
      </div>
    </div>
  );
}

/**
 * Shared search-result card (reference layout: date, provider, route timeline, price, details).
 */
export default function ResultOfferCard({
  dateLabel,
  providerName,
  providerLogo,
  providerFallback,
  routeLeft,
  routeRight,
  routeDuration,
  routeStops,
  outboundLabel = 'Departing',
  returnRoute,
  price,
  pricePerLabel = 'Sample price per person',
  priceFinePrint = 'Not a live price — an advisor confirms the real fare',
  detailsTitle = 'Details',
  detailRows = [],
  bookNowProps,
  selectLabel = 'Request this option',
}) {
  const [open, setOpen] = useState(false);
  const [logoOk, setLogoOk] = useState(Boolean(providerLogo));
  const priceParts = formatPriceParts(price.amount, price.currency || 'USD');

  useEffect(() => {
    setLogoOk(Boolean(providerLogo));
  }, [providerLogo]);
  const displayCurrency = price.currency === 'USD' ? 'USD' : price.currency;

  return (
    <article className="result-offer-card">
      <div className="result-offer-card__body">
        {dateLabel && <p className="result-offer-card__date">{dateLabel}</p>}

        <div className="result-offer-card__provider">
          <div className="result-offer-card__logo" aria-hidden>
            {providerLogo && logoOk ? (
              <img src={providerLogo} alt="" onError={() => setLogoOk(false)} />
            ) : (
              <span className="result-offer-card__logo-fallback">
                {providerFallback || providerName?.slice(0, 2)?.toUpperCase() || '—'}
              </span>
            )}
          </div>
          <span className="result-offer-card__provider-name">{providerName}</span>
        </div>

        <div className="result-offer-card__legs">
          <p className="result-offer-card__leg-label">{outboundLabel}</p>
          <OfferRoute
            left={routeLeft}
            right={routeRight}
            duration={routeDuration}
            stops={routeStops}
          />
          {returnRoute && (
            <>
              <p className="result-offer-card__leg-label">{returnRoute.label || 'Returning'}</p>
              <OfferRoute
                left={returnRoute.left}
                right={returnRoute.right}
                duration={returnRoute.duration}
                stops={returnRoute.stops}
              />
            </>
          )}
        </div>

        <div className="result-offer-card__divider" aria-hidden />

        <div className="result-offer-card__footer">
          <div className="result-offer-card__price-block">
            <p className="result-offer-card__price">
              <span className="result-offer-card__currency">{displayCurrency}</span>{' '}
              <span className="result-offer-card__amount">
                {priceParts.symbol === '$' ? priceParts.symbol : ''}
                {priceParts.whole}
                <sup className="result-offer-card__cents">.{priceParts.cents}</sup>
                {priceParts.symbol !== '$' ? ` ${priceParts.symbol}` : ''}
              </span>
            </p>
            <p className="result-offer-card__price-label">{pricePerLabel}</p>
            <p className="result-offer-card__price-fine">{priceFinePrint}</p>
          </div>
          <BookNowButton
            serviceType={bookNowProps?.serviceType}
            bookingPayload={bookNowProps?.bookingPayload}
            label={selectLabel}
            className="result-offer-card__select btn-select"
          />
        </div>
      </div>

      {detailRows.length > 0 && (
        <div className="result-offer-card__expand">
          <button
            type="button"
            className={`result-offer-card__expand-btn ${open ? 'is-open' : ''}`}
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {detailsTitle}
            <ChevronDown size={18} aria-hidden />
          </button>
          {open && (
            <dl className="result-offer-card__details">
              {detailRows.map(({ label, value }) =>
                value != null && String(value).trim() !== '' ? (
                  <div key={label} className="result-offer-card__detail-row">
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ) : null
              )}
            </dl>
          )}
        </div>
      )}
    </article>
  );
}
